<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import BaseModal from './BaseModal.vue';
import type { RoomTypeImage } from '@/types/schemas/nbc';

interface Props {
  open: boolean;
  images: RoomTypeImage[];
  initialIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  close: [];
}>();

const currentIndex = ref(props.initialIndex);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      currentIndex.value = props.initialIndex;
    }
  }
);

const currentImage = computed(() => {
  return props.images[currentIndex.value];
});

const hasPrevious = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < props.images.length - 1);

const imageCounter = computed(() => `${currentIndex.value + 1} / ${props.images.length}`);

function goToPrevious() {
  if (hasPrevious.value) {
    currentIndex.value--;
  }
}

function goToNext() {
  if (hasNext.value) {
    currentIndex.value++;
  }
}

function handleClose() {
  emit('update:open', false);
  emit('close');
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.open) return;

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      goToPrevious();
      break;
    case 'ArrowRight':
      event.preventDefault();
      goToNext();
      break;
    case 'Escape':
      event.preventDefault();
      handleClose();
      break;
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <BaseModal
    :open="open"
    variant="lightbox"
    size="full"
    :show-close-button="false"
    :no-padding="true"
    @update:open="emit('update:open', $event)"
    @close="handleClose"
  >
    <div class="relative w-full h-full flex items-center justify-center bg-black">
      <button
        type="button"
        class="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors"
        aria-label="Close lightbox"
        @click="handleClose"
      >
        <i class="w-4 h-4 bg-white icon icon-x"></i>
      </button>

      <div
        v-if="images.length > 1"
        class="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-black/50 rounded-full text-white text-sm"
      >
        {{ imageCounter }}
      </div>

      <button
        v-if="hasPrevious"
        type="button"
        class="absolute left-4 z-20 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors"
        aria-label="Previous image"
        @click="goToPrevious"
      >
        <i class="w-6 h-6 bg-white icon icon-chevron-right transform !rotate-[270deg]"></i>
      </button>

      <button
        v-if="hasNext"
        type="button"
        class="absolute right-4 z-20 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors"
        aria-label="Next image"
        @click="goToNext"
      >
        <i class="w-6 h-6 bg-white icon icon-chevron-right"></i>
      </button>

      <div class="relative max-w-full max-h-full p-12 flex items-center justify-center">
        <img
          :src="currentImage.large"
          :alt="currentImage.caption || 'Room image'"
          class="max-w-full max-h-[80vh] object-contain"
        />
      </div>

      <div
        v-if="currentImage.caption"
        class="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 max-w-2xl px-6 py-3 bg-black/70 rounded-lg text-white text-center"
      >
        {{ currentImage.caption }}
      </div>
    </div>
  </BaseModal>
</template>
